import React from "react";
import LegalShell, { LegalSection } from "./legal/LegalShell";
import { LEGAL } from "../lib/legalConfig";
import { Mail, Shield, AlertOctagon } from "lucide-react";

/**
 * /support — minimal contact + escalation page. Uses the LegalShell layout
 * for visual consistency with the legal corpus.
 */
export default function SupportPage() {
  return (
    <LegalShell title="Help & Support" testid="support-page"
      intro={<>We aim to respond to every email within one business day. Use the channel that matches your need — it gets your message to the right person fastest.</>}>
      <LegalSection id="account-orders" n={1} title="Account & order help">
        <p>For day-to-day account, order, or payment questions, email <a href={`mailto:${LEGAL.SUPPORT_EMAIL}`} className="text-primary underline" data-testid="support-email-link">{LEGAL.SUPPORT_EMAIL}</a>. Include your account email in the subject line so we can find your record quickly.</p>
        <Box icon={Mail} colour="primary">
          {LEGAL.SUPPORT_EMAIL}
        </Box>
      </LegalSection>

      <LegalSection id="kyc-sof" n={2} title="Verification, KYC, or Source of Funds">
        <p>If you're stuck on identity verification or have been asked for Source of Funds documents, the fastest path is to reply directly to the email that asked for the document. Otherwise: <a href={`mailto:${LEGAL.SUPPORT_EMAIL}`} className="text-primary underline">{LEGAL.SUPPORT_EMAIL}</a> with "KYC" or "SoF" in the subject.</p>
      </LegalSection>

      <LegalSection id="complaints" n={3} title="Formal complaints">
        <p>If you have a formal complaint about how we've handled your account, your data, or a specific order, please use the dedicated complaints channel — it is routed to a separate reviewer and tracked on a regulated complaints log.</p>
        <Box icon={Shield} colour="amber">
          {LEGAL.COMPLAINTS_EMAIL}
        </Box>
      </LegalSection>

      <LegalSection id="security" n={4} title="Security disclosures">
        <p>If you believe you've found a security vulnerability, please disclose it responsibly to <a href={`mailto:security@xchangely.io`} className="text-primary underline">security@xchangely.io</a>. We will acknowledge within 24 hours. We do not currently run a paid bug-bounty programme but coordinate disclosure publicly with credit.</p>
        <Box icon={AlertOctagon} colour="rose">
          security@xchangely.io
        </Box>
      </LegalSection>

      <LegalSection id="data" n={5} title="Data-protection enquiries">
        <p>For data access, deletion, or other GDPR rights requests, contact our Data Protection Officer at <a href={`mailto:${LEGAL.DPO_EMAIL}`} className="text-primary underline">{LEGAL.DPO_EMAIL}</a>. We will respond within the statutory 30-day window.</p>
      </LegalSection>
    </LegalShell>
  );
}

const Box = ({ icon: Icon, colour, children }) => {
  const colours = {
    primary: "bg-primary/10 text-primary border-primary/30",
    amber:   "bg-amber-50 text-amber-900 border-amber-300",
    rose:    "bg-rose-50 text-rose-900 border-rose-300",
  };
  return (
    <div className={`mt-3 rounded-xl border px-3 py-2 inline-flex items-center gap-2 font-mono text-sm ${colours[colour] || colours.primary}`}>
      <Icon className="w-4 h-4" />
      {children}
    </div>
  );
};
